import { Box, Typography } from "@outschool/backpack";
import { useTranslation } from "@outschool/localization";
import { TrackedButton } from "@outschool/ui-components-shared";
import { Checkbox, LoadingModal } from "@outschool/ui-legacy-component-library";
import React from "react";

import * as Env from "../../../shared/Env";
import ErrorMessage from "../ErrorMessage";
import {
  WaitlistLearnerType,
  WaitlistSeatType,
  WaitlistSectionType
} from "../WaitlistedClassesList";
import WaitlistSectionSummaryString from "./WaitlistSectionSummaryString";

export interface LeaveWaitlistFormProps {
  key: string;
  learner: WaitlistLearnerType;
  activity: WaitlistSectionType["activity"];
  waitlistSeats: WaitlistSeatType[];
  error: string;
  handleUpdateWaitlist: (seatsToRemove: string[]) => void;
}

export default function LeaveWaitlistForm({
  learner,
  activity,
  waitlistSeats,
  error,
  handleUpdateWaitlist
}: LeaveWaitlistFormProps) {
  const { t } = useTranslation(
    "client\\components\\waitlists\\SectionWaitlistForm"
  );

  const [submitted, setSubmitted] = React.useState<boolean>(false);
  const [localError, setLocalError] = React.useState<string>();

  const [sectionItems, setSectionItems] = React.useState<SectionItemType[]>(
    getWaitlistSectionItems(waitlistSeats)
  );

  const submit = (e: MouseEvent) => {
    e.preventDefault();
    if (submitted) {
      return;
    }
    const selectedSections = sectionItems.filter(si => !!si.checked);
    if (!selectedSections.length) {
      setLocalError(t("Please select at least one section."));
      return;
    }
    const seatsToRemove = selectedSections.map(si => si.seatUid);
    handleUpdateWaitlist(seatsToRemove);
    setSubmitted(true);
  };

  if (!activity || !activity.details) {
    return <LoadingModal />;
  }

  return (
    <form method="POST" onSubmit={e => e.preventDefault()}>
      {(error || localError) && (
        <ErrorMessage
          value={error || localError}
          showStatusPageLink={Env.IS_READ_ONLY_MODE}
        />
      )}
      <Typography variant="h5" component="h3">
        {t("Class Sections {{learnerName}} Is Waitlisted On", {
          learnerName: learner.name
        })}
      </Typography>
      <WaitlistSectionItems
        sectionItems={sectionItems}
        onSectionItemsUpdate={setSectionItems}
        clearErrors={() => setLocalError("")}
      />
      <TrackedButton
        variant="contained"
        fullWidth
        disabled={submitted}
        onClick={submit}
        trackingUniqueId={learner.uid}
        trackingName="leave_waitlist"
      >
        {t("Remove From Selected Waitlist(s)")}
      </TrackedButton>
    </form>
  );
}

interface SectionItemType {
  seatUid: string;
  checked: boolean;
  sectionText: React.ReactNode;
}
function getWaitlistSectionItems(
  waitlistSeats: WaitlistSeatType[]
): SectionItemType[] {
  return waitlistSeats.map(seat => {
    const sectionText = (
      <WaitlistSectionSummaryString
        section={seat.section}
        activity={seat.section.activity}
      />
    );
    return {
      seatUid: seat.uid,
      checked: false,
      sectionText
    };
  });
}

function WaitlistSectionItems({
  sectionItems,
  onSectionItemsUpdate,
  clearErrors
}: {
  sectionItems: SectionItemType[];
  onSectionItemsUpdate: (sectionItems: SectionItemType[]) => void;
  clearErrors: () => void;
}) {
  function handleUpdate(seatUid: string, checked: boolean) {
    const updatedSections = sectionItems.map(item =>
      item.seatUid === seatUid ? { ...item, checked } : item
    );
    onSectionItemsUpdate(updatedSections);
    clearErrors();
  }

  return (
    <Box sx={{ marginY: "2rem" }}>
      {sectionItems.map(({ seatUid, checked, sectionText }) => {
        return (
          <Box
            flex
            key={seatUid}
            sx={{
              alignItems: "center",
              padding: "1rem",
              marginY: "1rem",
              backgroundColor: "grey.50",
              borderRadius: "8px"
            }}
          >
            <Checkbox
              id={seatUid}
              checked={checked}
              onChange={e => {
                handleUpdate(seatUid, e.target.checked);
              }}
              sx={{ marginRight: "0.6rem" }}
            />
            <Box>{sectionText}</Box>
          </Box>
        );
      })}
    </Box>
  );
}
